import { formatDate } from "@/lib/formatters";
import { memo, useState, useCallback } from "react";
import {
  Plus,
  Trash2,
  Loader2,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import Button from "@mui/material/Button";
import IconButton from "@/components/common/GoogleIconButton";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { cn } from "@/lib/utils";
import type { ChatConversation } from "@/types";

interface ChatHistoryListProps {
  chats: ChatConversation[];
  activeChatId: string | null;
  onNewChat: () => void;
  onSelectChat: (chat: ChatConversation) => void;
  onDeleteChat: (chatId: string) => void;
  hasMore: boolean;
  loading: boolean;
  loadingMore?: boolean;
  error?: Error | null;
  onLoadMore: () => void;
  onRetry?: () => void;
}

export const ChatHistoryList = memo(function ChatHistoryList({
  chats,
  activeChatId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  hasMore,
  loading,
  loadingMore = false,
  error,
  onLoadMore,
  onRetry,
}: ChatHistoryListProps) {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleDelete = useCallback(() => {
    if (deleteTarget) {
      onDeleteChat(deleteTarget);
      setDeleteTarget(null);
    }
  }, [deleteTarget, onDeleteChat]);

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="p-3 sm:p-4 shrink-0">
          <Button
            variant="contained"
            onClick={onNewChat}
            className="w-full gap-2"
            size="small"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 sm:px-3 pb-2 sm:pb-3">
          <div className="space-y-0.5">
            {chats.map((chat) => {
              const isActive = String(chat.id) === activeChatId;
              return (
                <div
                  key={chat.id}
                  className={cn(
                    "group flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )}
                  onClick={() => onSelectChat(chat)}
                  role="button"
                  tabIndex={0}
                  aria-current={isActive ? "true" : undefined}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectChat(chat);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className={cn("truncate", isActive && "font-medium")}>
                      {chat.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(chat.created)}
                    </div>
                  </div>
                  <IconButton
                    type="submit"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(String(chat.id));
                    }}
                    className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200 cursor-pointer"
                    aria-label={`Delete ${chat.title}`}
                    sx={{
                      padding: "0.375rem",
                      borderRadius: "0.5rem",
                      color: "inherit",
                      "&:hover": {
                        backgroundColor: "var(--destructive-10)",
                        color: "var(--destructive)",
                      },
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              );
            })}
            {hasMore && (
              <Button
                variant="text"
                size="small"
                onClick={onLoadMore}
                disabled={loading || loadingMore}
                aria-busy={loading || loadingMore}
                aria-label={loadingMore ? "Loading more chats" : undefined}
                className="w-full"
                sx={{ color: "var(--muted-foreground)" }}
              >
                {loading || loadingMore ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  "Load more"
                )}
              </Button>
            )}
            {error && chats.length > 0 && !loadingMore && (
              <p role="alert" className="px-3 py-2 text-sm text-destructive">
                Could not load more chats. Select Load more to try again.
              </p>
            )}
            {error && chats.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  Couldn't load chats
                </p>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  {error.message}
                </p>
                {onRetry && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onRetry}
                    className="gap-1.5"
                  >
                    <Loader2 className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                )}
              </div>
            )}
            {!error && chats.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-3">
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  No conversations yet
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Start a new chat to begin
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete chat?"
        message="This will permanently delete this chat and its messages."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
      />
    </>
  );
});
